import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import ReactDOM from 'react-dom';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { SEASHORE_BLUE, DESERT_FLOWER, PEANUT_BUTTER_ORANGE, COOL_GREEN, DARK_GREY } from '../../dashboardColors';
import './MinimizedView.scss';

const MinimizedView = ({
  notifications = [],
  onAlertClick,
  highlightedAlertId,
  onEventStateChange,
  onEventPriorityChange,
  eventStateOptions = [],
  eventPriorityOptions = [],
  selectedPriorityFilter = [],
  selectedEventStateFilter = []
}, context) => {
  const tableContainerRef = useRef(null);
  const highlightedRowRef = useRef(null);
  const [localStates, setLocalStates] = useState({});
  const [localPriorities, setLocalPriorities] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const matchesPriorityFilter = (alert, filters) => {
    const priorityText = (alert.priority || '').toLowerCase();
    return filters.some(filter => priorityText.includes(filter === 'medium' ? 'med' : filter));
  };

  const filteredNotifications = useMemo(() => {
    if (!notifications?.length) return [];

    let filtered = notifications;

    if (selectedPriorityFilter.length > 0) {
      filtered = filtered.filter(alert => matchesPriorityFilter(alert, selectedPriorityFilter));
    }

    if (selectedEventStateFilter.length > 0) {
      filtered = filtered.filter(alert => {
        const alertEventState = localStates[alert.id] ?? alert.eventStateValue ?? 0;
        return selectedEventStateFilter.includes(alertEventState);
      });
    }

    // Sort based on sortConfig
    const sorted = [...filtered];

    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'alertId':
            aValue = a.alertId || 0;
            bValue = b.alertId || 0;
            break;
          case 'priority':
            aValue = localPriorities[a.id] ?? a.priorityValue ?? 999;
            bValue = localPriorities[b.id] ?? b.priorityValue ?? 999;
            break;
          case 'alertStatus':
            aValue = (a.alertStatus || '').toLowerCase();
            bValue = (b.alertStatus || '').toLowerCase();
            break;
          case 'probability':
            aValue = a.probability || 0;
            bValue = b.probability || 0;
            break;
          case 'eventState':
            aValue = localStates[a.id] ?? a.eventStateValue ?? 0;
            bValue = localStates[b.id] ?? b.eventStateValue ?? 0;
            break;
          case 'comment':
            aValue = (a.comment || '').toLowerCase();
            bValue = (b.comment || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return sorted;
  }, [notifications, selectedPriorityFilter, selectedEventStateFilter, localStates, localPriorities, sortConfig]);

  const getIndicatorIcon = (indicator) => {
    if (!indicator) return null;

    const indicatorLower = indicator.toLowerCase();
    const iconProps = { fontSize: 28, fontWeight: 'bold', strokeWidth: 2 };

    switch (indicatorLower) {
      case 'burst':
      case 'pipe burst':
        return <WarningIcon sx={{ ...iconProps, color: '#d32f2f' }} />;
      case 'growing up':
        return <TrendingUpIcon sx={{ ...iconProps, color: '#d32f2f' }} />;
      case 'growing down':
      case 'fading':
        return <TrendingDownIcon sx={{ ...iconProps, color: '#4caf50' }} />;
      case 'no event':
        return <CloseIcon sx={{ ...iconProps, color: '#9e9e9e' }} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (highlightedAlertId && highlightedRowRef.current && tableContainerRef.current) {
      const container = tableContainerRef.current;
      const row = highlightedRowRef.current;
      const containerRect = container.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();

      if (rowRect.top < containerRect.top || rowRect.bottom > containerRect.bottom) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedAlertId, filteredNotifications]);

  const handleStateChange = (alertId, value) => {
    setLocalStates(prev => ({ ...prev, [alertId]: value }));
    onEventStateChange && onEventStateChange(alertId, value);
  };

  const handlePriorityChange = (alertId, value) => {
    setLocalPriorities(prev => ({ ...prev, [alertId]: value }));
    onEventPriorityChange && onEventPriorityChange(alertId, value);
  };

  const getPriorityColor = (priorityValue) => {
    // Use numeric priorityValue for color
    // Database: 0 = low, 1 = medium, 2 = high
    // Colors: low = green, medium = orange, high = red
    const numValue = typeof priorityValue === 'number' ? priorityValue : parseInt(priorityValue, 10);
    if (numValue === 0) return COOL_GREEN;       // low = green
    if (numValue === 1) return PEANUT_BUTTER_ORANGE;  // medium = orange
    if (numValue === 2) return DESERT_FLOWER;   // high = red
    return DARK_GREY;
  };

  const handleSort = (columnKey) => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  const renderSortableHeader = (columnKey, label) => {
    const isSorted = sortConfig.key === columnKey;
    return (
      <th
        className={`sortable-header ${isSorted ? 'sorted' : ''}`}
        onClick={() => handleSort(columnKey)}
      >
        <span className="header-content">
          {label}
          <span className={`sort-icon ${isSorted ? 'active' : ''}`}>
            {isSorted && sortConfig.direction === 'desc' ? (
              <ArrowDownwardIcon fontSize="small" />
            ) : (
              <ArrowUpwardIcon fontSize="small" />
            )}
          </span>
        </span>
      </th>
    );
  };

  const InlineDropdown = ({ value, options, onChange, renderLabel, buttonStyle }) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    const closeMenu = useCallback(() => setOpen(false), []);

    const openMenu = useCallback((e) => {
      e?.stopPropagation?.();
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      setOpen(true);
    }, []);

    const toggleMenu = useCallback((e) => {
      if (open) {
        e?.stopPropagation?.();
        setOpen(false);
      } else {
        openMenu(e);
      }
    }, [open, openMenu]);

    useEffect(() => {
      if (!open) return;
      const handleOutside = (ev) => {
        const btn = buttonRef.current;
        const menuEl = menuRef.current;
        if (!btn) return closeMenu();
        if (btn.contains(ev.target) || (menuEl && menuEl.contains(ev.target))) return;
        closeMenu();
      };
      const handleKey = (ev) => {
        if (ev.key === 'Escape') {
          ev.stopPropagation();
          closeMenu();
        }
      };
      const handleRelayout = () => {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      };
      const attach = () => {
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside, { passive: true });
      };
      const timer = setTimeout(attach, 0);
      window.addEventListener('resize', handleRelayout);
      window.addEventListener('scroll', handleRelayout, true);
      document.addEventListener('keydown', handleKey, true);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleOutside);
        document.removeEventListener('touchstart', handleOutside);
        window.removeEventListener('resize', handleRelayout);
        window.removeEventListener('scroll', handleRelayout, true);
        document.removeEventListener('keydown', handleKey, true);
      };
    }, [open, closeMenu]);

    const menuNode = (
      <div
        role="listbox"
        ref={menuRef}
        style={{
          position: 'fixed',
          top: menuPos.top,
          left: menuPos.left,
          minWidth: Math.max(menuPos.width, 100),
          maxHeight: 300,
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: 6,
          boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
          zIndex: 20050
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {options.map(opt => (
          <div
            key={opt.value}
            role="option"
            aria-selected={String(opt.value) === String(value)}
            onClick={() => { onChange?.(opt.value); closeMenu(); }}
            style={{
              padding: '6px 10px',
              cursor: 'pointer',
              fontSize: '1.2rem',
              background: String(opt.value) === String(value) ? '#f0f4ff' : '#fff'
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {opt.label}
          </div>
        ))}
      </div>
    );

    return (
      <span style={{ display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
        <button
          ref={buttonRef}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); toggleMenu(e); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMenu(e); } if (e.key === 'ArrowDown' && !open) { e.preventDefault(); openMenu(e); } }}
          style={{
            width: '100%',
            textAlign: 'center',
            padding: '4px 8px',
            borderRadius: 6,
            border: '1px solid #cfd8dc',
            background: '#fff',
            cursor: 'pointer',
            ...buttonStyle
          }}
        >
          <span>{renderLabel ? renderLabel() : options.find(o => String(o.value) === String(value))?.label}</span>
          <span style={{ float: 'right', opacity: 0.6 }}>▾</span>
        </button>
        {open && ReactDOM.createPortal(menuNode, document.body)}
      </span>
    );
  };

  return (
    <Box className="minimized-view">
      <Box className="notifications-table-container" ref={tableContainerRef}>
        <table className="minimized-notifications-table">
          <thead>
            <tr>
              <th className="indicator-header"></th>
              {renderSortableHeader('alertId', context.t('alert_id'))}
              {renderSortableHeader('priority', context.t('event_priority'))}
              {renderSortableHeader('alertStatus', context.t('alert_status'))}
              {renderSortableHeader('probability', context.t('probability'))}
              {renderSortableHeader('eventState', context.t('event_state'))}
              {renderSortableHeader('comment', context.t('comment'))}
            </tr>
          </thead>
          <tbody>
            {filteredNotifications.map((notification) => {
              const isHighlighted = highlightedAlertId === notification.alertId;
              return (
                <tr
                  key={notification.id || notification.alertId}
                  className={`notification-row ${isHighlighted ? 'highlighted' : ''}`}
                  onClick={() => onAlertClick(notification.alertId)}
                  ref={isHighlighted ? highlightedRowRef : null}
                >
                  <td className="indicator-cell">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {getIndicatorIcon(notification.indicator)}
                    </Box>
                  </td>
                  <td className="alert-id-cell">
                    <Typography
                      variant="body1"
                      className="alert-id"
                      sx={{ fontSize: '1.3rem', fontWeight: 700, color: SEASHORE_BLUE }}
                    >
                      {notification.alertId}
                    </Typography>
                  </td>
                  <td className="priority-cell" onClick={(e) => e.stopPropagation()}>
                    <InlineDropdown
                      value={
                        localPriorities[notification.id || notification.alertId] !== undefined
                          ? localPriorities[notification.id || notification.alertId]
                          : (typeof notification.priorityValue === 'number' ? notification.priorityValue : '')
                      }
                      options={eventPriorityOptions}
                      onChange={(val) => handlePriorityChange(notification.id || notification.alertId, val)}
                      renderLabel={() => {
                        // Get the current numeric value
                        const currentValue = localPriorities[notification.id || notification.alertId] !== undefined
                          ? localPriorities[notification.id || notification.alertId]
                          : notification.priorityValue;

                        const selectedOption = eventPriorityOptions.find(o => o.value === currentValue);

                        return (
                          <span style={{ fontWeight: 800, color: getPriorityColor(currentValue) }}>
                            {selectedOption?.label || notification.priority || ''}
                          </span>
                        );
                      }}
                      buttonStyle={{ fontSize: '1.2rem', minWidth: '115px' }}
                    />
                  </td>
                  <td className="status-cell">
                    <Typography variant="body1" sx={{ fontSize: '1.2rem', color: '#000', fontWeight: 700 }}>
                      {notification.alertStatus}
                    </Typography>
                  </td>
                  <td className="probability-cell">
                    <Typography variant="body1" sx={{ fontSize: '1.4rem', color: DARK_GREY, fontWeight: 800 }}>
                      {notification.probability}
                    </Typography>
                  </td>
                  <td className="event-state-cell" onClick={(e) => e.stopPropagation()}>
                    <InlineDropdown
                      value={
                        localStates[notification.id || notification.alertId] !== undefined
                          ? localStates[notification.id || notification.alertId]
                          : (typeof notification.eventStateValue === 'number' ? notification.eventStateValue : 0)
                      }
                      options={eventStateOptions}
                      onChange={(val) => handleStateChange(notification.id || notification.alertId, val)}
                      renderLabel={() => (
                        <span style={{ fontWeight: 700 }}>{notification.eventState}</span>
                      )}
                      buttonStyle={{ fontSize: '1.2rem', minWidth: '120px' }}
                    />
                  </td>
                  <td className="comment-cell">
                    <Typography
                      variant="body1"
                      sx={{ fontSize: '1.22rem', color: DARK_GREY, fontWeight: 500, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={notification.comment || ''}
                    >
                      {notification.comment || ''}
                    </Typography>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

MinimizedView.propTypes = {
  notifications: PropTypes.array,
  onAlertClick: PropTypes.func.isRequired,
  highlightedAlertId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEventStateChange: PropTypes.func,
  onEventPriorityChange: PropTypes.func,
  eventStateOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  eventPriorityOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  selectedPriorityFilter: PropTypes.array,
  selectedEventStateFilter: PropTypes.array
};

MinimizedView.contextTypes = {
  t: PropTypes.func.isRequired
};

export default MinimizedView;
